/**
 * The brief contract (blueprint §4.7): the shape BriefHero and PillarBrief
 * render. Owned by Wave 1 and frozen in Wave 2: composers may add fields,
 * never rename or narrow these.
 *
 * Page slices never read brief fields directly. They hand composer output to
 * BriefHero or PillarBrief, which call `toActionBrief()`, so copy changes and
 * page changes stay decoupled. Every composer emits these fields natively;
 * the legacy kicker/headlineSubject/headlineModifier/weakLink/deck mapping
 * was deleted with those fields in Wave 3.
 */

export type BriefPillar = 'compliance' | 'bpa' | 'identity'

export interface ActionBriefFields {
  /** The h1: action first, period-terminated. */
  headline: string
  /** A substring of `headline` set at 400 against the 300 headline. `pillar` ONLY when `text` is that pillar's display name (Subject-Ink Rule). */
  emphasis?: { text: string; pillar?: BriefPillar }
  /** One sentence with exactly one **bold** run naming the subject and its count. */
  lede: string
  /** The view's one Soft Green button. */
  primaryAction?: { label: string; href: string }
}

export type ActionBrief = ActionBriefFields & { verdict?: string }

const PILLARS: readonly BriefPillar[] = ['compliance', 'bpa', 'identity']

type Bag = Record<string, unknown>
const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined)

/**
 * The brief fields of any composer output, normalized: blank strings and
 * half-specified emphasis or actions are dropped, and a missing headline or
 * lede reads as empty. Never throws.
 */
export function toActionBrief(b: unknown): ActionBrief {
  if (!b || typeof b !== 'object') return { headline: '', lede: '' }
  const bag = b as Bag
  const out: ActionBrief = { headline: str(bag.headline) ?? '', lede: str(bag.lede) ?? '' }
  const e = bag.emphasis as Bag | undefined
  const eText = e && str(e.text)
  if (eText) {
    out.emphasis = { text: eText }
    if (typeof e.pillar === 'string' && (PILLARS as readonly string[]).includes(e.pillar)) out.emphasis.pillar = e.pillar as BriefPillar
  }
  const a = bag.primaryAction as Bag | undefined
  const aLabel = a && str(a.label), aHref = a && str(a.href)
  if (aLabel && aHref) out.primaryAction = { label: aLabel, href: aHref }
  const verdict = str(bag.verdict)
  if (verdict) out.verdict = verdict
  return out
}
